import React, { useState, useEffect } from "react";
import { showMoney, cents } from "../Data/Money";
import { createApiService } from "../shared/apiConfig";
import { FeesApi, ServerRequestsAvailableOptInFee, DataEntitiesFinanceOperatorFeeScope, DataEntitiesFinanceOperatorFeePaymentType, DataEntitiesFinanceOperatorFeeCalc } from "../shared/api/axios-client";

export interface OptInFeesSelection {
    [operatorFeeId: number]: boolean;
}

export interface OptInFeesSelectorProps {
    facilityId: number;
    feeScope: DataEntitiesFinanceOperatorFeeScope; // 0 = hourly, 1 = monthly
    feePaymentType: DataEntitiesFinanceOperatorFeePaymentType;
    onSelectionChange: (selection: OptInFeesSelection) => void;
    onFeesLoaded?: (fees: ServerRequestsAvailableOptInFee[]) => void;
    isCheckout: boolean; // Only load opt-in fees if true
    disabled?: boolean; // Prevent changes when parent is disabled
}

export function OptInFeesSelector({ facilityId, feeScope, feePaymentType, onSelectionChange, onFeesLoaded, isCheckout, disabled = false }: OptInFeesSelectorProps) {
    const [optInFees, setOptInFees] = useState<ServerRequestsAvailableOptInFee[]>([]);
    const [selection, setSelection] = useState<OptInFeesSelection>({});
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!isCheckout) {
            // Don't load opt-in fees if not in checkout mode
            setOptInFees([]);
            setSelection({});
            onSelectionChange({});
            setIsLoading(false);
            return;
        }

        const fetchOptInFees = async () => {
            setIsLoading(true);
            try {
                const feesApi = createApiService(FeesApi);
                const response = await feesApi.getOptInFees({
                    facilityId,
                    feeScope,
                    feePaymentType
                });
                
                const fees = response.data.availableOptInFees || [];
                setOptInFees(fees);
                
                        // Set default selections
                        const defaultSelection: OptInFeesSelection = {};
                        fees.forEach(fee => {
                            defaultSelection[fee.operatorFeeId || 0] = fee.isDefaultOptIn || false;
                        });
                        setSelection(defaultSelection);
                        onSelectionChange(defaultSelection);
                onFeesLoaded?.(fees);
            } catch (error) {
                console.error("Error fetching opt-in fees:", error);
                setOptInFees([]);
                // Still call onSelectionChange with empty selection to prevent blocking
                onSelectionChange({});
            }
            setIsLoading(false);
        };

        fetchOptInFees();
    }, [facilityId, feeScope, feePaymentType, isCheckout]);

    const handleSelectionChange = (operatorFeeId: number, checked: boolean) => {
        const newSelection = {
            ...selection,
            [operatorFeeId]: checked
        };
        setSelection(newSelection);
        onSelectionChange(newSelection);
    };

    const formatAmount = (fee: ServerRequestsAvailableOptInFee) => {
        if (fee.operatorFeeCalc === DataEntitiesFinanceOperatorFeeCalc.NUMBER_1) {
            // Percent calculation
            return `${fee.amount}%`;
        } else {
            // Flat rate calculation
            return showMoney(cents(fee.amount || 0));
        }
    };

    if (isLoading) {
        return (
            <div className="bg-white rounded-3 shadow overflow-hidden mb-3">
                <div className="px-3 py-2 text-light" 
                     style={{ background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)' }}>
                    <i className="fas fa-plus-circle me-2"></i>
                    Loading Options...
                </div>
                <div className="px-3 py-2">
                    <div className="d-flex justify-content-center">
                        <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (optInFees.length === 0) {
        return null; // Don't render anything if no opt-in fees available
    }

    return (
        <div className="bg-white rounded-3 shadow overflow-hidden mb-3">
            <div className="px-1 py-1 " 
                 style={{ background: 'linear-gradient(135deg,rgb(230, 230, 230) 0%,rgb(197, 197, 197) 100%)' }}>
                <i className="fas fa-plus-circle me-2"></i>
                Options
            </div>
            <div className="px-3 py-2">
                {optInFees.map((fee, index) => (
                    <div key={fee.operatorFeeId} className={`${index < optInFees.length - 1 ? 'mb-3' : ''}`}>
                        {/* Header row with name, price, and toggle */}
                        <div className="d-flex justify-content-between align-items-start">
                            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center">
                                <span className="fw-bold">{fee.name || 'Recommended Service'}</span>
                                <span className="fw-medium ms-sm-2">
                                    {formatAmount(fee)}{feeScope === DataEntitiesFinanceOperatorFeeScope.NUMBER_0 ? '' : '/mo'}
                                </span>
                            </div>
                            
                            {/* Bootstrap form-switch toggle */}
                            <div className="form-check form-switch">
                                                                       <input
                                           className="form-check-input"
                                           type="checkbox"
                                           role="switch"
                                           id={`opt-in-fee-${fee.operatorFeeId}`}
                                           checked={selection[fee.operatorFeeId || 0] || false}
                                           disabled={disabled}
                                           onChange={(e) => handleSelectionChange(fee.operatorFeeId || 0, e.target.checked)}
                                       />
                            </div>
                        </div>
                        
                        {/* Description row */}
                        {fee.description && (
                            <div 
                                className="text-muted small p-1"
                                style={{ fontSize: '0.8rem', marginLeft:'-5px', marginBottom:'-10px' }}
                                dangerouslySetInnerHTML={{ __html: fee.description }}
                            />
                        )}
                    </div>
                ))}
                
            </div>
        </div>
    );
}